import { supabase } from '../lib/supabase';

const BUCKET = 'profile-media';
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function getExt(mime) {
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}

export const storageService = {
  async uploadAvatar(userId, file) {
    if (!supabase) throw new Error('Supabase not configured');
    if (!file || !file.type || !ALLOWED_TYPES.includes(file.type)) {
      throw new Error('Allowed types: JPEG, PNG, WebP');
    }
    if (file.size > MAX_SIZE_BYTES) {
      throw new Error('File must be under 2MB');
    }
    const ext = getExt(file.type);
    const path = `${userId}/avatar.${ext}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
      upsert: true,
      contentType: file.type,
    });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return urlData.publicUrl;
  },

  async uploadCover(userId, file) {
    if (!supabase) throw new Error('Supabase not configured');
    if (!file || !file.type || !ALLOWED_TYPES.includes(file.type)) {
      throw new Error('Allowed types: JPEG, PNG, WebP');
    }
    if (file.size > MAX_SIZE_BYTES) {
      throw new Error('File must be under 2MB');
    }
    const ext = getExt(file.type);
    const path = `${userId}/cover.${ext}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
      upsert: true,
      contentType: file.type,
    });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return urlData.publicUrl;
  },

  async removeAvatar(userId) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data: list } = await supabase.storage.from(BUCKET).list(userId);
    if (!list?.length) return;
    const avatar = list.find((f) => f.name?.startsWith('avatar.'));
    if (avatar) {
      await supabase.storage.from(BUCKET).remove([`${userId}/${avatar.name}`]);
    }
  },

  async removeCover(userId) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data: list } = await supabase.storage.from(BUCKET).list(userId);
    if (!list?.length) return;
    const cover = list.find((f) => f.name?.startsWith('cover.'));
    if (cover) {
      await supabase.storage.from(BUCKET).remove([`${userId}/${cover.name}`]);
    }
  },
};

const VERIFICATION_BUCKET = 'verification-docs';
const MAX_VERIFICATION_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_VERIFICATION_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

export const verificationStorageService = {
  async uploadDocument(userId, file, documentType = 'id') {
    if (!supabase) throw new Error('Supabase not configured');
    if (!file?.type || !ALLOWED_VERIFICATION_TYPES.includes(file.type)) {
      throw new Error('Allowed: JPEG, PNG, PDF');
    }
    if (file.size > MAX_VERIFICATION_BYTES) {
      throw new Error('File must be under 5MB');
    }
    const ext = file.name?.split('.').pop() || (file.type === 'application/pdf' ? 'pdf' : 'jpg');
    const path = `${userId}/${documentType}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from(VERIFICATION_BUCKET).upload(path, file, {
      upsert: false,
      contentType: file.type,
    });
    if (error) throw error;
    return path;
  },

  getSignedUrl(filePath, expiresIn = 3600) {
    if (!supabase) return Promise.resolve({ data: { signedUrl: null }, error: new Error('Supabase not configured') });
    return supabase.storage.from(VERIFICATION_BUCKET).createSignedUrl(filePath, expiresIn);
  },
};
